import type { LibraryItem, ProcessingStatus } from '@/shared/types';
import { applyLibraryDefaults, fetchLibraryFromApi, mergeMetadata } from '@/features/library/services/libraryApi';
import { logger } from '@/shared/utils/logger';
import { isArray, readJson, storageKey, writeJson } from '@/shared/storage/namespacedStorage';

// Per-user localStorage namespace. Keyed by the backend-issued user_id so each
// account sees only its own items on a shared browser. This module is the single
// seam to later swap for a backend GET /videos call.
const STORAGE_PREFIX = 'valoi-library-items';

const key = (userId: string) => storageKey(STORAGE_PREFIX, userId);

const safeReadStorage = (userId: string): LibraryItem[] =>
  readJson<LibraryItem[]>(key(userId), [], isArray);

const persistStorage = (userId: string, items: LibraryItem[]) =>
  writeJson(key(userId), items, 'library items');

export function upsertLibraryItem(userId: string, item: LibraryItem) {
  const existing = safeReadStorage(userId);
  const withoutCurrent = existing.filter((entry) => entry.id !== item.id);
  const next = [...withoutCurrent, item];
  persistStorage(userId, next);
  return item;
}

export function updateLibraryItemStatus(
  userId: string,
  id: string,
  status: ProcessingStatus,
  updates: Partial<Pick<LibraryItem, 'videoUrl' | 'description' | 'durationSeconds' | 'sourceText' | 'metadata' | 'thumbnailUrl' | 'vttUrl'>>,
) {
  /* An own property holding `undefined` still wins a spread, so `{ videoUrl:
     undefined }` used to blank a videoUrl the cache already held. That is a
     patch, not a replacement — a caller passing a field it does not know about
     is saying nothing, not saying "none". Two call sites already worked around
     this by hand (videoService merges before it writes, ContentPage's mirror
     spreads conditionally) and a third did not, so a completion frame carrying
     no download_url erased a good URL. Fixed here rather than at each caller,
     because the next one will make the same assumption.

     Clearing a field is therefore not expressible through this function. No
     caller wants it: `status` is the only thing any of them replaces outright,
     and it is a required argument for exactly that reason. */
  const patch = Object.fromEntries(
    Object.entries(updates).filter(([, value]) => value !== undefined),
  );

  const existing = safeReadStorage(userId);
  const next = existing.map((entry) => {
    if (entry.id !== id) return entry;
    return {
      ...entry,
      ...patch,
      status,
    };
  });
  persistStorage(userId, next);
}

export function fetchLibraryItem(userId: string, id: string) {
  return safeReadStorage(userId).find((entry) => entry.id === id);
}

export function removeLibraryItem(userId: string, id: string) {
  const existing = safeReadStorage(userId);
  const next = existing.filter((entry) => entry.id !== id);
  persistStorage(userId, next);
}

// Where the returned items came from:
//  'api'   — a successful backend fetch.
//  'cache' — the backend was unreachable but we had cached items to show.
//  'error' — the backend was unreachable and there is nothing cached to fall
//            back to, so the UI should surface a real error (not an empty list).
export type LibrarySource = 'api' | 'cache' | 'error';

export type LibraryFetchResult = {
  items: LibraryItem[];
  source: LibrarySource;
};

const byNewestFirst = (a: LibraryItem, b: LibraryItem) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

// How long a cache-only item (one a SUCCESSFUL fetch didn't return) is kept.
//
// It exists purely to cover the submit race: videoService upserts the item
// locally the moment a job is submitted, so a list fetch that was already in
// flight can legitimately come back without it. Outside that window, a
// successful fetch omitting an item means the item is genuinely gone from the
// backend — deleted, or belonging to a different environment than the one the
// frontend now points at — and keeping it renders a card that can never load
// (and, for a non-terminal status, a phantom job that appears stuck forever).
const LOCAL_ONLY_GRACE_MS = 10 * 60 * 1000;

export async function fetchLibraryItems(userId: string): Promise<LibraryFetchResult> {
  const localEntries = safeReadStorage(userId);

  try {
    // Pass the user_id through for the legacy 'video' backend (?user_id= query).
    // The deployed 'videos' backend ignores it — identity now comes from the
    // Bearer JWT (IDENTITY_FROM_JWT=true), so no X-User-Id header is sent.
    const apiEntries = await fetchLibraryFromApi(userId);
    logger.log(`Library API returned ${apiEntries.length} items`);

    // Field-wise merge — the backend is authoritative. For every field the API
    // actually returns we take its value; we fall back to the richer cached value
    // only for the fields the (currently minimal) response omits. The API mapper
    // leaves omitted fields undefined (see ApiLibraryItem), so `?? local` adopts
    // real enrichments the instant the backend ships them — no code change needed.
    // An item with no API counterpart keeps its cached value (see localOnly
    // below); an item with no cached counterpart gets display defaults applied.
    const localById = new Map(localEntries.map((item) => [item.id, item]));
    const mergedFromApi = apiEntries.map((apiItem) => {
      const local = localById.get(apiItem.id);
      if (!local) return applyLibraryDefaults(apiItem);
      return {
        ...local,
        title: apiItem.title ?? local.title,
        description: apiItem.description ?? local.description,
        status: apiItem.status ?? local.status,
        videoUrl: apiItem.videoUrl ?? local.videoUrl,
        sourceText: apiItem.sourceText ?? local.sourceText,
        aspectRatio: apiItem.aspectRatio ?? local.aspectRatio,
        durationSeconds: apiItem.durationSeconds ?? local.durationSeconds,
        createdAt: apiItem.createdAt ?? local.createdAt,
        // Presigned enrichments (thumbnail poster, WebVTT caption track). The
        // /videos list is complete and authoritative for these today, so take its
        // value directly — a fresh presigned URL, or undefined when the server
        // reports none. `?? local` would (a) let the `...local` spread's stale URL
        // survive and (b) resurrect an expired presigned URL when the API says
        // there is none. The cache is only the offline fallback (catch branch).
        //
        // CONTRACT DEPENDENCY: this trusts every successful /videos response to
        // carry thumbnail_url/vtt_url whenever they exist (see
        // docs/captions-contract.md — vtt_url is present for completed storytelling
        // jobs on the list endpoint). A degraded/partial response that omits a
        // field it *should* have sent would blank the cached URL here (both the
        // mapper and the API collapse null → undefined, so we can't tell an
        // intentional "no captions" from an accidental omission). We accept that
        // over the alternative — `?? local` silently serving an expired URL that
        // 403s in the player — which is the stale-cache bug this PR set out to fix.
        thumbnailUrl: apiItem.thumbnailUrl,
        vttUrl: apiItem.vttUrl,
        metadata: mergeMetadata(local.metadata, apiItem.metadata),
      };
    });

    // The API is authoritative for WHICH items exist. Anything it omits is kept
    // only inside the submit-race grace window (see LOCAL_ONLY_GRACE_MS); older
    // stragglers are evicted so server-side deletions actually reach the UI.
    // An unparseable/absent createdAt counts as stale — applyLibraryDefaults
    // always stamps one, so a missing value means the entry predates that.
    const apiIds = new Set(apiEntries.map((item) => item.id));
    const cutoff = Date.now() - LOCAL_ONLY_GRACE_MS;
    const localOnly: LibraryItem[] = [];
    const evicted: LibraryItem[] = [];
    for (const item of localEntries) {
      if (apiIds.has(item.id)) continue;
      const createdAt = new Date(item.createdAt).getTime();
      (Number.isFinite(createdAt) && createdAt >= cutoff ? localOnly : evicted).push(item);
    }
    if (evicted.length > 0) {
      logger.log(
        `Library evicted ${evicted.length} cached item(s) the backend no longer returns: ` +
          evicted.map((item) => `${item.id} (${item.status})`).join(', '),
      );
    }

    const merged = [...mergedFromApi, ...localOnly];

    // Persist merged results to this user's namespace for offline access.
    persistStorage(userId, merged);

    return { items: merged.sort(byNewestFirst), source: 'api' };
  } catch {
    logger.log('Library API unavailable, falling back to localStorage');
    return {
      items: localEntries.sort(byNewestFirst),
      // Only call it a cache hit if we actually have something to show.
      source: localEntries.length > 0 ? 'cache' : 'error',
    };
  }
}
